// src/api/axiosInstance.js
import axios from "axios";
import safeToast from "../utils/safeToast";
import { createBrowserHistory } from "history";
import { API_URL } from "../config/api";
import { decryptText } from "../config/encryption";

const history = createBrowserHistory();

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use((config) => {
  // List of public endpoints that don't require authentication
  const publicEndpoints = [
    '/Auth/CheckUser',
    '/Auth/CheckStaffByLoginCode',
    '/Auth/login',
    '/Auth/register',
    '/Auth/staff-login',
    '/AdminMaster/get-alumnus-detail-invitation',
  ];
  
  // Check if this is a public endpoint
  const isPublicEndpoint = publicEndpoints.some(endpoint => 
    config.url?.includes(endpoint) || config.url?.endsWith(endpoint)
  );
  
  // Skip adding auth headers for public endpoints
  if (isPublicEndpoint) {
    // console.log(`[axiosInstance] Skipping auth header for public endpoint: ${config.url}`);
    return config;
  }
  
  // Check for regular user session first
  let user = localStorage.getItem("UserLogDetails");
  let sessionType = "user";
  
  // If no regular user session, check for admin session
  if (!user) {
    user = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    sessionType = "admin";
  }
  
  if (user) {
    try {
      const decrypted = decryptText(user);
      if (!decrypted) {
        console.error(`[axiosInstance] Failed to decrypt ${sessionType} session for request: ${config.url}`);
        return config;
      }
      
      const parsed = JSON.parse(decrypted);
      // Try multiple possible token field names
      const token = parsed.Token || parsed.token || parsed.TOKEN || parsed.accessToken || parsed.access_token;
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Debug log to verify token is being added
        // console.log(`[axiosInstance] Token added to request (${sessionType}): ${config.url}`);
        // console.log(`[axiosInstance] Token preview: ${token.substring(0, 20)}...`);
      } else {
        // console.error(`[axiosInstance] No token found in ${sessionType} session for request: ${config.url}`);
        // console.error(`[axiosInstance] Available keys in session:`, Object.keys(parsed));
      }
    } catch (e) {
      console.error(`[axiosInstance] Token parse error (${sessionType}):`, e);
      console.error(`[axiosInstance] Encrypted session length:`, user ? user.length : 0);
    }
  } else {
    console.warn(`[axiosInstance] No session found in localStorage for request: ${config.url}`);
  }
  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // console.log("Response Interceptor triggered", error.response?.status); // Add this
    if (error.response?.status === 401) {
      // List of public endpoints that may return 401 for "not found" reasons
      const publicEndpoints = [
        '/Auth/CheckUser',
        '/Auth/CheckStaffByLoginCode',
        '/Auth/login',
        '/Auth/register',
        '/Auth/staff-login',
        '/AdminMaster/get-alumnus-detail-invitation',
      ];
      
      // Check if this is a public endpoint
      const isPublicEndpoint = publicEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint) || error.config?.url?.endsWith(endpoint)
      );
      
      // For public endpoints, 401 might mean "user not found" - don't clear session or redirect
      if (isPublicEndpoint) {
        // console.log(`[axiosInstance] 401 on public endpoint - likely "not found" response: ${error.config?.url}`);
        return Promise.reject(error);
      }
      
      // For protected endpoints, 401 means authentication failed
      // Disconnect SignalR connection before logout
      try {
        const signalRManager = (await import("../services/signalRManager")).default;
        if (signalRManager && signalRManager.getConnection()) {
          await signalRManager.disconnect();
        }
      } catch (err) {
        console.error("Error disconnecting SignalR:", err);
      }
      
      safeToast.error("Session expired. Please log in again.");
      localStorage.clear();
      window.location.href = "/BasicAuth"; // Force full reload
    }
    return Promise.reject(error);
  }
);


export default instance;
